import { apiClient, ApiResponse } from './api-client'
import type { MenuCategory } from '../models/menu'

export interface CreateMenuCategoryData {
  businessId: number
  name: string
  description?: string
}

export interface UpdateMenuCategoryData {
  name?: string
  description?: string
  isVisible?: boolean
}

export class MenuCategoryService {
  /**
   * Get all categories for a business
   */
  async getCategories(businessId: string): Promise<MenuCategory[]> {
    const response = await apiClient.get<ApiResponse<{ categories: MenuCategory[] }>>(
      `/menus/categories/business/${businessId}`
    )
    if (response.data.success && response.data.data) {
      return response.data.data.categories || []
    }
    throw new Error(response.data.error?.message || 'Failed to get categories')
  }

  /**
   * Create new category
   */
  async createCategory(data: CreateMenuCategoryData): Promise<MenuCategory> {
    const response = await apiClient.post<ApiResponse<{ category: MenuCategory }>>('/menus/categories', data)
    if (response.data.success && response.data.data) {
      return response.data.data.category
    }
    throw new Error(response.data.error?.message || 'Failed to create category')
  }

  /**
   * Update category
   */
  async updateCategory(id: string, data: UpdateMenuCategoryData): Promise<MenuCategory> {
    const response = await apiClient.put<ApiResponse<{ category: MenuCategory }>>(`/menus/categories/${id}`, data)
    if (response.data.success && response.data.data) {
      return response.data.data.category
    }
    throw new Error(response.data.error?.message || 'Failed to update category')
  }

  /**
   * Delete category
   */
  async deleteCategory(id: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse>(`/menus/categories/${id}`)
      if (!response.data.success) {
        const errorMessage = response.data.error?.message || 'فشل حذف التصنيف'
        const error = new Error(errorMessage)
        ;(error as any).response = { data: response.data }
        throw error
      }
    } catch (error: any) {
      // Preserve the original error response if available
      if (error.response) {
        throw error
      }
      // If it's a network error or other issue, wrap it
      const errorMessage = error.message || 'حدث خطأ أثناء حذف التصنيف'
      const newError = new Error(errorMessage)
      ;(newError as any).response = error.response
      throw newError
    }
  }

  /**
   * Toggle category visibility
   */
  async toggleCategory(id: string): Promise<MenuCategory> {
    const response = await apiClient.patch<ApiResponse<{ category: MenuCategory }>>(`/menus/categories/${id}/toggle`)
    if (response.data.success && response.data.data) {
      return response.data.data.category
    }
    throw new Error(response.data.error?.message || 'Failed to toggle category')
  }
}

// Export singleton instance
export const menuCategoryService = new MenuCategoryService()

